import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Rating,
  Breadcrumbs,
  Link,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  Zoom,
  Fade,
  Slide,
  Grow
} from '@mui/material';
import { styled, alpha, keyframes } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowBack as ArrowBackIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  ShoppingBag as ShoppingBagIcon,
  Share as ShareIcon,
  LocalShipping as LocalShippingIcon,
  Verified as VerifiedIcon,
  Diamond as DiamondIcon,
  Security as SecurityIcon,
  Loop as LoopIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ZoomIn as ZoomInIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';
import { demoProducts } from '../data/demoData';
import './ProductDetail.css';

// Animations
// const shimmer = keyframes`
//   0% { background-position: -1000px 0; }
//   100% { background-position: 1000px 0; }
// `;

// const float = keyframes`
//   0% { transform: translateY(0px); }
//   50% { transform: translateY(-10px); }
//   100% { transform: translateY(0px); }
// `;

const glow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(198, 164, 63, 0.3); }
  70% { box-shadow: 0 0 0 15px rgba(198, 164, 63, 0); }
  100% { box-shadow: 0 0 0 0 rgba(198, 164, 63, 0); }
`;

// Styled Components
const GlassCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(198, 164, 63, 0.2)',
  borderRadius: '24px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  padding: theme.spacing(3),
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: 'rgba(198, 164, 63, 0.5)',
    boxShadow: '0 30px 60px rgba(198, 164, 63, 0.15)',
  }
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  borderRadius: '20px',
  overflow: 'hidden',
  backgroundColor: '#FAF7F2',
  border: '1px solid rgba(198, 164, 63, 0.2)',
  '&:hover .carousel-controls': {
    opacity: 1,
  }
}));

const MainImage = styled(motion.img)({
  width: '100%',
  height: '500px',
  objectFit: 'cover',
  cursor: 'zoom-in',
  transition: 'transform 0.3s ease',
  '@media (max-width: 768px)': {
    height: '350px',
  }
});

const ThumbnailImage = styled(motion.img)({
  width: '80px',
  height: '80px',
  objectFit: 'cover',
  borderRadius: '12px',
  cursor: 'pointer',
  border: '2px solid transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 10px 20px rgba(198, 164, 63, 0.2)',
  },
  '&.active': {
    borderColor: '#C6A43F',
    boxShadow: '0 0 0 4px rgba(198, 164, 63, 0.2)',
  }
});

const ZoomOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(10, 15, 30, 0.95)',
  backdropFilter: 'blur(10px)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'zoom-out',
});

const ZoomImage = styled(motion.img)({
  maxWidth: '90%',
  maxHeight: '90%',
  objectFit: 'contain',
  borderRadius: '12px',
  boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
});

const StyledChip = styled(Chip)(({ theme, customcolor }) => ({
  backgroundColor: alpha(customcolor || '#C6A43F', 0.1),
  color: customcolor || '#C6A43F',
  fontWeight: 600,
  fontSize: '0.8rem',
  padding: theme.spacing(0.5),
  '& .MuiChip-label': {
    letterSpacing: '1px',
  }
}));

const PriceTypography = styled(Typography)({
  fontFamily: 'Cormorant Garamond',
  fontWeight: 700,
  color: '#0A0F1E',
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -5,
    left: 0,
    width: '60px',
    height: '3px',
    background: 'linear-gradient(90deg, #C6A43F, #E5D5AC)',
    borderRadius: '2px',
  }
});

const FeatureIcon = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px',
  borderRadius: '16px',
  backgroundColor: 'rgba(198, 164, 63, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(198, 164, 63, 0.1)',
    transform: 'translateY(-5px)',
  }
});

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  // const [showZoomControls, setShowZoomControls] = useState(false);

  // Demo product images carousel
  const productImages = [
    'https://images.unsplash.com/photo-1605100804763-247f67b3557e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1602751584552-8ba73aad10e1?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1602173574767-37ac01994b2a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const foundProduct = demoProducts.find(p => p.id === parseInt(id)) || demoProducts[0];
      setProduct(foundProduct);
      // Set related products (same category)
      setRelatedProducts(demoProducts.filter(p => p.category === foundProduct.category && p.id !== foundProduct.id).slice(0, 4));
      setLoading(false);
    }, 1000);
  }, [id]);

  const handleNextImage = () => {
    setCurrentImage((prev) => (prev + 1) % productImages.length);
  };

  const handlePrevImage = () => {
    setCurrentImage((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const handleThumbnailClick = (index) => {
    setCurrentImage(index);
  };

  const handleZoomOpen = () => {
    setZoomOpen(true);
  };

  const handleZoomClose = () => {
    setZoomOpen(false);
  };

  const handleFavoriteToggle = () => {
    setIsFavorite(!isFavorite);
  };

  const handleQuantityChange = (delta) => {
    setQuantity(Math.max(1, quantity + delta));
  };

  const handleAddToCart = () => {
    console.log('Added to cart:', { ...product, quantity });
    // Show success message
  };

  const handleBuyNow = () => {
    console.log('Buy now:', { ...product, quantity });
    // Navigate to checkout
  };

  if (loading) {
    return (
      <Box className="product-detail-loading">
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <DiamondIcon sx={{ fontSize: 80, color: '#C6A43F', opacity: 0.5 }} />
            </motion.div>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box className="product-detail-error">
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h3" sx={{ fontFamily: 'Cormorant Garamond', color: '#C6A43F' }}>
              Product Not Found
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/shop')}
              sx={{ mt: 3, color: '#C6A43F' }}
            >
              Back to Shop
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="product-detail-page">
      {/* Breadcrumbs */}
      <Container maxWidth="lg" sx={{ pt: 12, pb: 4 }}>
        <Fade in={true} timeout={1000}>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link 
              color="inherit" 
              href="/"
              sx={{ 
                textDecoration: 'none',
                '&:hover': { color: '#C6A43F' }
              }}
            >
              Home
            </Link>
            <Link 
              color="inherit" 
              href="/shop"
              sx={{ 
                textDecoration: 'none',
                '&:hover': { color: '#C6A43F' }
              }}
            >
              Shop
            </Link>
            <Link
              color="inherit"
              href={`/shop?category=${product.category}`}
              sx={{ 
                textDecoration: 'none',
                '&:hover': { color: '#C6A43F' }
              }}
            >
              {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
            </Link>
            <Typography color="text.primary">{product.name}</Typography>
          </Breadcrumbs>
        </Fade>

        <Grid container spacing={6}>
          {/* Left Column - Image Carousel */}
          <Grid item xs={12} md={7}>
            <Slide direction="right" in={true} timeout={800}>
              <Box>
                {/* Main Carousel */}
                <CarouselContainer>
                  <MainImage
                    src={productImages[currentImage]}
                    alt={product.name}
                    onClick={handleZoomOpen}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ scale: 1.02 }}
                  />
                  
                  {/* Carousel Controls */}
                  <Box className="carousel-controls" sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  }}>
                    <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                      <IconButton
                        onClick={handlePrevImage}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.9)',
                          backdropFilter: 'blur(5px)',
                          '&:hover': { bgcolor: 'white' }
                        }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                      <IconButton
                        onClick={handleNextImage}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.9)',
                          backdropFilter: 'blur(5px)',
                          '&:hover': { bgcolor: 'white' }
                        }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </motion.div>
                  </Box>

                  {/* Zoom Indicator */}
                  <Tooltip title="Click to zoom">
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(5px)',
                        '&:hover': { bgcolor: 'white' }
                      }}
                      onClick={handleZoomOpen}
                    >
                      <ZoomInIcon />
                    </IconButton>
                  </Tooltip>

                  {/* Favorite Button */}
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                    }}
                  >
                    <IconButton
                      onClick={handleFavoriteToggle}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(5px)',
                        '&:hover': { bgcolor: 'white' }
                      }}
                    >
                      {isFavorite ? (
                        <FavoriteIcon sx={{ color: '#C6A43F' }} />
                      ) : (
                        <FavoriteBorderIcon />
                      )}
                    </IconButton>
                  </motion.div>

                  {/* Product Badges */}
                  {product.isNew && (
                    <Zoom in={true}>
                      <Chip
                        label="NEW"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          left: 16,
                          bgcolor: '#C6A43F',
                          color: '#0A0F1E',
                          fontWeight: 700,
                          letterSpacing: '2px',
                          animation: `${glow} 2s infinite`,
                        }}
                      />
                    </Zoom>
                  )}
                </CarouselContainer>

                {/* Thumbnail Strip */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  mt: 3, 
                  overflowX: 'auto',
                  pb: 1,
                  '&::-webkit-scrollbar': {
                    height: 6,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(198, 164, 63, 0.1)',
                    borderRadius: 3,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#C6A43F',
                    borderRadius: 3,
                  }
                }}>
                  {productImages.map((img, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ThumbnailImage
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        className={currentImage === index ? 'active' : ''}
                        onClick={() => handleThumbnailClick(index)}
                      />
                    </motion.div>
                  ))}
                </Box>

                {/* Zoom Modal */}
                <AnimatePresence>
                  {zoomOpen && (
                    <ZoomOverlay onClick={handleZoomClose}>
                      <ZoomImage
                        src={productImages[currentImage]}
                        alt={product.name}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        transition={{ type: "spring", damping: 25 }}
                      />
                    </ZoomOverlay>
                  )}
                </AnimatePresence>
              </Box>
            </Slide>
          </Grid>

          {/* Right Column - Product Info */}
          <Grid item xs={12} md={5}>
            <Slide direction="left" in={true} timeout={800}>
              <Box>
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <GlassCard>
                    {/* Category & Availability */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <StyledChip
                        label={product.category.toUpperCase()}
                        icon={<DiamondIcon sx={{ fontSize: 16 }} />}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          width: 10, 
                          height: 10, 
                          borderRadius: '50%', 
                          bgcolor: product.stock > 0 ? '#4CAF50' : '#f44336',
                          animation: product.stock > 0 ? `${glow} 2s infinite` : 'none'
                        }} />
                        <Typography variant="body2" color="text.secondary">
                          {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Product Name */}
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontFamily: 'Cormorant Garamond', 
                        fontWeight: 700,
                        mb: 1,
                        color: '#0A0F1E'
                      }}
                    >
                      {product.name}
                    </Typography>

                    {/* Rating */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Rating 
                        value={product.rating || 4.5} 
                        precision={0.5} 
                        readOnly 
                        sx={{
                          '& .MuiRating-iconFilled': {
                            color: '#C6A43F',
                          }
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        ({product.reviews || 124} reviews)
                      </Typography>
                    </Box>

                    {/* Price */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Price
                      </Typography>
                      <PriceTypography variant="h2">
                        ₹{product.finalPrice?.toLocaleString()}
                      </PriceTypography>
                      {product.type !== 'fixed' && (
                        <Typography variant="caption" sx={{ color: '#C6A43F', display: 'block', mt: 1 }}>
                          ✧ Dynamic pricing based on daily metal rate
                        </Typography>
                      )}
                    </Box>

                    {/* Metal Details */}
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: 2,
                      mb: 3,
                      p: 2,
                      bgcolor: 'rgba(198, 164, 63, 0.05)',
                      borderRadius: '16px'
                    }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Metal
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {product.metalPurity?.toUpperCase() || '22K'} Gold
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Weight
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {product.metalWeight || 12.5}g
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          SKU
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          MIR-{product.id}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Description */}
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                      {product.description || 'A timeless masterpiece crafted in hallmarked 22k gold. Features intricate handcrafted details that capture the essence of eternal elegance.'}
                    </Typography>

                    {/* Quantity Selector */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Quantity:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(198,164,63,0.3)', borderRadius: '8px' }}>
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                          <IconButton size="small" onClick={() => handleQuantityChange(-1)}>
                            -
                          </IconButton>
                        </motion.div>
                        <Typography sx={{ mx: 2, minWidth: 30, textAlign: 'center' }}>
                          {quantity}
                        </Typography>
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                          <IconButton size="small" onClick={() => handleQuantityChange(1)}>
                            +
                          </IconButton>
                        </motion.div>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {product.stock} available
                      </Typography>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        style={{ flex: 1 }}
                      >
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<ShoppingBagIcon />}
                          onClick={handleAddToCart}
                          sx={{
                            bgcolor: '#0A0F1E',
                            color: 'white',
                            py: 1.8,
                            '&:hover': {
                              bgcolor: '#C6A43F',
                              boxShadow: '0 10px 30px rgba(198, 164, 63, 0.4)'
                            }
                          }}
                        >
                          Add to Cart
                        </Button>
                      </motion.div>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        style={{ flex: 1 }}
                      >
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleBuyNow}
                          sx={{
                            borderColor: '#C6A43F',
                            color: '#C6A43F',
                            py: 1.8,
                            '&:hover': {
                              borderColor: '#B3923A',
                              backgroundColor: 'rgba(198, 164, 63, 0.04)'
                            }
                          }}
                        >
                          Buy Now
                        </Button>
                      </motion.div>
                    </Box>

                    {/* Share & Save */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Share on WhatsApp">
                          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <IconButton size="small" sx={{ color: '#25D366' }}>
                              <WhatsAppIcon />
                            </IconButton>
                          </motion.div>
                        </Tooltip>
                        <Tooltip title="Share on Instagram">
                          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <IconButton size="small" sx={{ color: '#E4405F' }}>
                              <InstagramIcon />
                            </IconButton>
                          </motion.div>
                        </Tooltip>
                        <Tooltip title="Share">
                          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <IconButton size="small" sx={{ color: '#666' }}>
                              <ShareIcon />
                            </IconButton>
                          </motion.div>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Hallmarked & Certified
                      </Typography>
                    </Box>
                  </GlassCard>
                </motion.div>
              </Box>
            </Slide>
          </Grid>

          {/* Product Details Tabs */}
          <Grid item xs={12}>
            <Grow in={true} timeout={1000}>
              <Box sx={{ mt: 6 }}>
                <GlassCard>
                  <Tabs
                    value={tabValue}
                    onChange={(e, v) => setTabValue(v)}
                    sx={{
                      borderBottom: '1px solid rgba(198, 164, 63, 0.2)',
                      mb: 3,
                      '& .MuiTab-root.Mui-selected': {
                        color: '#C6A43F',
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#C6A43F',
                      }
                    }}
                  >
                    <Tab label="Product Details" />
                    <Tab label="Specifications" />
                    <Tab label="Shipping & Returns" />
                    <Tab label="Reviews" />
                  </Tabs>

                  <AnimatePresence mode="wait">
                    {tabValue === 0 && (
                      <motion.div
                        key="details"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Typography variant="body1" paragraph>
                            {product.description || 'This exquisite piece is meticulously handcrafted by our master artisans, combining traditional techniques with contemporary design. Each detail is carefully considered to create a timeless treasure that will be cherished for generations.'}
                          </Typography>
                          <List>
                            {[
                              'Handcrafted by master artisans',
                              'Certified hallmarked gold',
                              'Natural diamonds (VS clarity, F color)',
                              'Includes authenticity certificate',
                              'Lifetime polishing service',
                              'Gift box included'
                            ].map((item, index) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <VerifiedIcon sx={{ color: '#C6A43F', fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </motion.div>
                    )}

                    {tabValue === 1 && (
                      <motion.div
                        key="specs"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', mb: 2, color: '#C6A43F' }}>
                                Metal Specifications
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Metal Type</Typography>
                                  <Typography fontWeight={600}>{product.metalType || 'Gold'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Purity</Typography>
                                  <Typography fontWeight={600}>{product.metalPurity || '22K'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Weight</Typography>
                                  <Typography fontWeight={600}>{product.metalWeight || 12.5}g</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Hallmark</Typography>
                                  <Typography fontWeight={600}>BIS Hallmarked</Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', mb: 2, color: '#C6A43F' }}>
                                Diamond Specifications
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Type</Typography>
                                  <Typography fontWeight={600}>Natural/Lab-Grown</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Carat</Typography>
                                  <Typography fontWeight={600}>0.50 ct</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Clarity</Typography>
                                  <Typography fontWeight={600}>VS1</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Color</Typography>
                                  <Typography fontWeight={600}>F</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography color="text.secondary">Certification</Typography>
                                  <Typography fontWeight={600}>IGI Certified</Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </motion.div>
                    )}

                    {tabValue === 2 && (
                      <motion.div
                        key="shipping"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Grid container spacing={4}>
                            <Grid item xs={12} md={4}>
                              <FeatureIcon>
                                <LocalShippingIcon sx={{ fontSize: 40, color: '#C6A43F', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', mb: 1 }}>
                                  Free Shipping
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center">
                                  On all orders above ₹50,000
                                </Typography>
                              </FeatureIcon>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FeatureIcon>
                                <LoopIcon sx={{ fontSize: 40, color: '#C6A43F', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', mb: 1 }}>
                                  30-Day Returns
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center">
                                  Easy returns within 30 days
                                </Typography>
                              </FeatureIcon>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FeatureIcon>
                                <SecurityIcon sx={{ fontSize: 40, color: '#C6A43F', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', mb: 1 }}>
                                  Secure Packaging
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center">
                                  Insured & tamper-proof delivery
                                </Typography>
                              </FeatureIcon>
                            </Grid>
                          </Grid>
                          <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(198,164,63,0.05)', borderRadius: '16px' }}>
                            <Typography variant="body2">
                              <strong>Estimated Delivery:</strong> 3-5 business days
                              <br />
                              <strong>Free Resizing:</strong> Within 30 days of purchase
                              <br />
                              <strong>Lifetime Warranty:</strong> On manufacturing defects
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                    )}

                    {tabValue === 3 && (
                      <motion.div
                        key="reviews"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 4 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h2" sx={{ color: '#C6A43F', fontWeight: 700 }}>
                                {product.rating || 4.5}
                              </Typography>
                              <Rating value={product.rating || 4.5} readOnly size="small" />
                              <Typography variant="body2" color="text.secondary">
                                {product.reviews || 124} reviews
                              </Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              sx={{ borderColor: '#C6A43F', color: '#C6A43F' }}
                            >
                              Write a Review
                            </Button>
                          </Box>
                          
                          {/* Sample Reviews */}
                          {[1, 2, 3].map((review) => (
                            <Box key={review} sx={{ mb: 3, pb: 3, borderBottom: '1px solid rgba(198,164,63,0.1)' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Avatar sx={{ bgcolor: alpha('#C6A43F', 0.2), color: '#C6A43F' }}>
                                  P
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2">Priya Sharma</Typography>
                                  <Rating value={5} readOnly size="small" />
                                </Box>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Absolutely stunning piece! The craftsmanship is exceptional and the diamond sparkles beautifully. 
                                Worth every penny.
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </GlassCard>
              </Box>
            </Grow>
          </Grid>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <Grid item xs={12}>
              <Fade in={true} timeout={1500}>
                <Box sx={{ mt: 4 }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontFamily: 'Cormorant Garamond', 
                      fontWeight: 700, 
                      mb: 4,
                      textAlign: 'center',
                      color: '#0A0F1E',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 80,
                        height: 3,
                        background: 'linear-gradient(90deg, #C6A43F, #E5D5AC)',
                        borderRadius: 2,
                      }
                    }}
                  >
                    You May Also Like
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {relatedProducts.map((product, index) => (
                      <Grid item xs={12} sm={6} md={3} key={product.id}>
                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -10 }}
                        >
                          <Paper
                            onClick={() => navigate(`/product/${product.id}`)}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              borderRadius: '16px',
                              border: '1px solid rgba(198,164,63,0.2)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: '#C6A43F',
                                boxShadow: '0 20px 40px rgba(198,164,63,0.15)',
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={productImages[index % productImages.length]}
                              alt={product.name}
                              sx={{
                                width: '100%',
                                height: 200,
                                objectFit: 'cover',
                                borderRadius: '12px',
                                mb: 2
                              }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {product.name}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="h6" sx={{ color: '#C6A43F', fontWeight: 700 }}>
                                ₹{product.finalPrice?.toLocaleString()}
                              </Typography>
                              <Chip 
                                label={product.category}
                                size="small"
                                sx={{ bgcolor: alpha('#C6A43F', 0.1), color: '#C6A43F' }}
                              />
                            </Box>
                          </Paper>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Fade>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductDetail;